// ============================================
// Entity Registry
// Canonical definitions of all legal entities
// referenced across the document library.
// Used by the GC Agent for cross-document
// entity consistency validation.
// ============================================

import type { EntityRegistry } from '../../types/docops';

export const ENTITY_REGISTRY: EntityRegistry = {
  entities: [
    // ── Operating Company (MSO) ──
    {
      id: 'operatingCompany',
      legalName: 'HemoLogix LLC',
      dba: 'HemoLogix',
      validReferences: [
        'HemoLogix LLC',
        'HemoLogix',
        'the Company',
        'the MSO',
      ],
      role: 'Operating company / MSO',
      roleDescription:
        'Technology platform company. Does NOT practice medicine. Does not employ physicians. Does not control clinical decisions.',
      appearsIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
        '1.3-consumer-health-data-policy',
        '1.4-hipaa-npp',
        '1.7-telehealth-informed-consent',
        '2.1-operating-agreement',
        '3.2-ip-assignment',
        '3.3-ip-license-agreement',
        '4.1-management-services-agreement',
        '5.1-baa-llc-pc',
        '7.1-employment-agreement',
      ],
    },

    // ── Telehealth Provider Network ──
    {
      id: 'telehealthProvider',
      legalName: 'OpenLoop Health, Inc.',
      dba: 'OpenLoop',
      validReferences: [
        'OpenLoop Health, Inc.',
        'OpenLoop',
        'the telehealth provider network',
        'the telehealth network',
      ],
      role: 'Telehealth provider network',
      roleDescription:
        'Nationwide telehealth provider network that provides clinical services through its own affiliated professional corporation(s) in each state.',
      appearsIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
        '4.1-management-services-agreement',
        '1.7-telehealth-informed-consent',
        '5.1-baa-llc-pc',
      ],
    },

    // ── Medical Director ──
    {
      id: 'medicalDirector',
      legalName: null,
      dba: null,
      validReferences: [
        'the Medical Director',
        '{{medicalDirectorName}}',
      ],
      role: 'Medical Director',
      roleDescription:
        'Provides clinical protocol oversight and quality assurance within HemoLogix LLC. Does NOT supervise or direct OpenLoop providers. Uses \'provides clinical oversight\' \u2014 never \'supervises.\'',
      appearsIn: [
        '1.1-terms-of-service',
        '4.1-management-services-agreement',
      ],
    },

    // ── IP Owner / Licensor ──
    {
      id: 'ipOwner',
      legalName: null,
      dba: null,
      validReferences: [
        'the IP Owner',
        'the intellectual property owner',
        'a third-party intellectual property owner',
        'the Licensor',
      ],
      role: 'IP Owner / Licensor',
      roleDescription:
        'Third-party owner of all Platform Technology. Licenses IP to HemoLogix LLC under exclusive license. Third-party beneficiary of TOS.',
      appearsIn: [
        '1.1-terms-of-service',
        '2.1-operating-agreement',
        '3.3-ip-license-agreement',
        '7.1-employment-agreement',
      ],
    },

    // ── Payment Processor ──
    {
      id: 'paymentProcessor',
      legalName: 'Stripe, Inc.',
      dba: 'Stripe',
      validReferences: [
        'Stripe, Inc.',
        'Stripe',
        'the payment processor',
      ],
      role: 'Payment processor',
      roleDescription:
        'Third-party payment processing for all platform transactions.',
      appearsIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
      ],
    },

    // ── Professional Corporation (dormant/backup) ──
    {
      id: 'professionalCorporation',
      legalName: null,
      dba: null,
      validReferences: [
        '{{pcEntityName}}',
        'the Professional Corporation',
        'the PC',
      ],
      role: 'Professional Corporation (dormant/backup)',
      roleDescription:
        'Backup PC entity. NOT actively referenced in user-facing documents. Clinical services provided through OpenLoop network directly.',
      appearsIn: [
        '4.1-management-services-agreement',
        '5.1-baa-llc-pc',
        '1.4-hipaa-npp',
      ],
    },

    // ── IP-Holding Trust ──
    {
      id: 'trustEntity',
      legalName: null,
      dba: null,
      validReferences: [
        '{{trustName}}',
        'the Trust',
        'the family trust',
      ],
      role: 'IP-holding trust',
      roleDescription:
        'Family trust that owns all intellectual property. Licenses to HemoLogix LLC. Referenced as \'family trust for estate planning purposes\' \u2014 never explain WHY.',
      appearsIn: [
        '3.1-trust-agreement',
        '3.2-ip-assignment',
        '3.3-ip-license-agreement',
        '2.1-operating-agreement',
      ],
    },
  ],
};
